import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCheck, Circle } from "lucide-react";
import client, { extractError } from "@/api";

const EVENT_TONE = {
  COMPANY_SUBMITTED: "info",
  NEW_COMPANY_SUBMISSION: "info",
  COMPANY_APPROVED: "success",
  COMPANY_REJECTED: "warning",
  COMPANY_SUSPENDED: "warning",
  COMPANY_REACTIVATED: "success",
};

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await client.get("/notifications");
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (err) {
      setError(extractError(err, "Unable to load notifications."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await client.post(`/notifications/${id}/read`); await load(); } catch (err) { setError(extractError(err)); }
  };

  const markAllRead = async () => {
    try { await client.post("/notifications/read-all"); await load(); } catch (err) { setError(extractError(err)); }
  };

  if (loading) return <div className="loading-block">Loading notifications…</div>;

  return (
    <div className="notifications-page" data-testid="notifications-page">
      <div className="notifications-toolbar">
        <div><strong data-testid="notifications-unread-count">{unread}</strong> unread</div>
        {unread > 0 && (
          <button type="button" className="ghost-button" data-testid="mark-all-read-button" onClick={markAllRead}>
            <CheckCheck size={14} /> Mark all as read
          </button>
        )}
      </div>
      {error && <div className="form-error">{error}</div>}
      {items.length === 0 ? (
        <div className="empty-state" data-testid="notifications-empty">
          <BellOff size={28} />
          <p>You have no notifications yet.</p>
        </div>
      ) : (
        <ul className="notifications-list">
          {items.map((n) => (
            <li key={n.id} className={n.is_read ? "read" : "unread"} data-testid={`notification-${n.id}`}>
              <div className="notification-icon"><Bell size={16} /></div>
              <div className="notification-body">
                <div className="notification-head">
                  <strong>{n.title}</strong>
                  <span className={`chip chip-${EVENT_TONE[n.event] || "info"}`}>{n.event.replaceAll("_", " ").toLowerCase()}</span>
                </div>
                <p>{n.message}</p>
                <small className="muted">{new Date(n.created_at).toLocaleString()}</small>
              </div>
              {!n.is_read && (
                <button type="button" className="link-button" data-testid={`mark-read-${n.id}`} onClick={() => markRead(n.id)}>
                  <Circle size={10} /> Mark read
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
